// src/pages/ResetPassword/ResetPasswordPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaEnvelope,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaInfoCircle,
  FaLock,
  FaShieldAlt,
  FaSignInAlt,
  FaUserPlus,
} from "react-icons/fa";

import useScrollToTop from "./../../hooks/useScollToTop";
import {
  checkResetPasswordToken,
  resetPassword,
} from "./../../services/passwordServices";
import { getSettings } from "./../../services/settingsServices";
import { toAbsolutePhotoUrl } from "./../../services/settingsPhotoServices";
import backgroundImage from "./HeaderSlider_02.jpg";

const DEFAULT_SETTINGS = {
  nom_club: "MonAppliClub",
  logo_club: "",
  header_url1: "",
  header_url2: "",
  header1_photo_url: "",
  header2_photo_url: "",
  couleur_principale: "",
  primary_color: "",
};

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function ResetPwd() {
  useScrollToTop();

  const navigate = useNavigate();
  const { token } = useParams();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [checkingToken, setCheckingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted || !clubSettings) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...clubSettings,
        });

        const primaryColor =
          clubSettings.couleur_principale || clubSettings.primary_color;

        if (primaryColor) {
          document.documentElement.style.setProperty(
            "--primary-color",
            primaryColor
          );
        }
      } catch (settingsError) {
        console.error("Erreur lors de la récupération des paramètres :", settingsError);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const verifyToken = async () => {
      setCheckingToken(true);
      setTokenValid(false);
      setError("");
      setInfo("");

      if (!token) {
        setError("Lien de réinitialisation manquant.");
        setCheckingToken(false);
        return;
      }

      try {
        const result = await checkResetPasswordToken(token);

        if (!mounted) return;

        if (!result?.valid) {
          setError(
            result?.message || "Lien de réinitialisation invalide ou expiré."
          );
          setTokenValid(false);
          return;
        }

        setTokenValid(true);
        setInfo(
          result?.message ||
            "Lien valide. Vous pouvez définir un nouveau mot de passe."
        );
      } catch (verifyError) {
        console.error("Erreur vérification token :", verifyError);

        if (mounted) {
          setError("Impossible de vérifier le lien pour le moment.");
          setTokenValid(false);
        }
      } finally {
        if (mounted) {
          setCheckingToken(false);
        }
      }
    };

    verifyToken();

    return () => {
      mounted = false;
    };
  }, [token]);

  const logoUrl = useMemo(() => {
    const logo = toAbsolutePhotoUrl(settings.logo_club?.trim() || "");
    return logo || "/img/logos/logo_monappliclub.png";
  }, [settings.logo_club]);

  const backgroundUrl = useMemo(() => {
    const candidate =
      settings.header_url2 ||
      settings.header_url1 ||
      settings.header2_photo_url ||
      settings.header1_photo_url;

    return toAbsolutePhotoUrl(candidate?.trim?.() || "") || backgroundImage;
  }, [
    settings.header_url1,
    settings.header_url2,
    settings.header1_photo_url,
    settings.header2_photo_url,
  ]);

  const passwordStrength = useMemo(() => {
    let score = 0;

    if (password.length >= 10) score += 1;
    if (/[A-ZÀ-Ý]/.test(password)) score += 1;
    if (/[a-zà-ÿ]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-zÀ-ÿ0-9]/.test(password)) score += 1;

    return score;
  }, [password]);

  const strengthLabel = useMemo(() => {
    if (!password) return "Minimum 10 caractères";
    if (passwordStrength <= 2) return "Sécurité faible";
    if (passwordStrength <= 4) return "Sécurité correcte";
    return "Sécurité forte";
  }, [password, passwordStrength]);

  const pwdOk = useMemo(() => password.length >= 10, [password]);

  const matchOk = useMemo(() => {
    return confirmPassword.length > 0 && password === confirmPassword;
  }, [password, confirmPassword]);

  const canSubmit =
    tokenValid && !checkingToken && !submitting && pwdOk && matchOk;

  const clearMessages = () => {
    if (error && tokenValid) setError("");
    if (info && !info.toLowerCase().includes("succès")) setInfo("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!tokenValid) {
      setError("Le lien de réinitialisation est invalide ou expiré.");
      return;
    }

    if (!pwdOk) {
      setError("Mot de passe : minimum 10 caractères.");
      return;
    }

    if (!matchOk) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    setError("");
    setInfo("");
    setSubmitting(true);

    try {
      const result = await resetPassword(token, password);

      if (result?.success) {
        setInfo(result?.message || "Mot de passe réinitialisé avec succès.");

        setTimeout(() => {
          navigate("/login");
        }, 1300);

        return;
      }

      setError(result?.message || "Réinitialisation impossible.");
    } catch (resetError) {
      console.error("Erreur réinitialisation mot de passe :", resetError);

      setError(
        resetError?.response?.data?.message ||
          "Réinitialisation impossible pour le moment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url("${backgroundUrl}")`,
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/92 via-slate-950/72 to-slate-950/60" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

      <div className="absolute left-[-120px] top-24 h-80 w-80 rounded-full bg-primary/25 blur-3xl" />
      <div className="absolute bottom-[-140px] right-[-100px] h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="w-full max-w-6xl">
          <div className="mb-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-bold text-white/80 backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white hover:text-slate-950"
            >
              <FaArrowLeft className="text-xs" />
              Retour
            </button>

            <Link
              to="/"
              className="hidden items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-bold text-white/80 backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white hover:text-slate-950 sm:inline-flex"
            >
              Accueil
            </Link>
          </div>

          <div className="grid overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/10 shadow-2xl backdrop-blur-2xl lg:grid-cols-[0.9fr_1.1fr]">
            <aside className="relative hidden overflow-hidden bg-slate-950/75 p-10 lg:block">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.20),transparent_36%)]" />

              <div className="relative flex h-full flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/70 backdrop-blur-xl">
                    <FaShieldAlt className="text-primary" />
                    Nouveau mot de passe
                  </div>

                  <h1 className="mt-7 font-barlow text-5xl font-bold leading-tight text-white">
                    Sécurisez à nouveau votre accès.
                  </h1>

                  <p className="mt-5 max-w-md text-base leading-8 text-white/70">
                    Choisissez un nouveau mot de passe pour retrouver l’accès à
                    votre espace personnel du club.
                  </p>
                </div>

                <div className="rounded-[2rem] border border-white/10 bg-white/10 p-5 backdrop-blur-xl">
                  <div className="flex items-center gap-4">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white p-2 shadow-xl">
                      <img
                        src={logoUrl}
                        alt={settings.nom_club || "MonAppliClub"}
                        className="h-full w-full object-contain"
                        draggable="false"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    </div>

                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
                        Site officiel
                      </p>

                      <p className="mt-1 truncate font-barlow text-2xl font-bold text-white">
                        {settings.nom_club || "MonAppliClub"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            <section className="relative bg-white p-6 text-slate-950 dark:bg-slate-900 dark:text-white sm:p-8 lg:p-10">
              <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

              <div className="relative mx-auto max-w-md">
                <div className="mb-8 text-center">
                  <div className="mx-auto flex h-24 w-24 items-center justify-center overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl dark:border-white/10">
                    <img
                      src={logoUrl}
                      alt={settings.nom_club || "MonAppliClub"}
                      className="h-full w-full object-contain"
                      draggable="false"
                      onError={(event) => {
                        event.currentTarget.src =
                          "/img/logos/logo_monappliclub.png";
                      }}
                    />
                  </div>

                  <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                    <FaLock />
                    Réinitialisation
                  </div>

                  <h2 className="mt-5 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white">
                    Nouveau mot de passe
                  </h2>

                  <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Définissez un mot de passe sécurisé pour votre compte.
                  </p>
                </div>

                {checkingToken && (
                  <div className="mb-5 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold leading-6 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                    <div className="flex items-start gap-3">
                      <span className="mt-1 h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      <span>Vérification du lien de réinitialisation...</span>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold leading-6 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                    <div className="flex items-start gap-3">
                      <FaExclamationTriangle className="mt-1 shrink-0" />
                      <span>{error}</span>
                    </div>
                  </div>
                )}

                {!error && info && (
                  <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold leading-6 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                    <div className="flex items-start gap-3">
                      <FaCheckCircle className="mt-1 shrink-0" />
                      <span>{info}</span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                    >
                      Nouveau mot de passe
                    </label>

                    <div className="relative">
                      <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Votre nouveau mot de passe"
                        className={cn(
                          "h-12 w-full rounded-2xl border py-3 pl-11 pr-14 text-sm font-semibold outline-none transition",
                          "bg-white text-slate-900 placeholder:text-slate-400",
                          "focus:ring-4 focus:ring-primary/10",
                          "dark:bg-slate-950/70 dark:text-white dark:placeholder:text-slate-500",
                          password.length && !pwdOk
                            ? "border-rose-300 focus:border-rose-500"
                            : "border-slate-200 focus:border-primary dark:border-white/10",
                          !tokenValid || checkingToken
                            ? "cursor-not-allowed opacity-60"
                            : ""
                        )}
                        value={password}
                        onChange={(event) => {
                          setPassword(event.target.value);
                          clearMessages();
                        }}
                        required
                        autoComplete="new-password"
                        disabled={!tokenValid || checkingToken || submitting}
                      />

                      <button
                        type="button"
                        onClick={() => setShowPassword((value) => !value)}
                        className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-white/10"
                        aria-label={
                          showPassword
                            ? "Masquer le mot de passe"
                            : "Afficher le mot de passe"
                        }
                        disabled={!tokenValid || checkingToken || submitting}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>

                    <div className="mt-3">
                      <div className="flex items-center justify-between gap-3">
                        <p
                          className={cn(
                            "text-xs font-semibold",
                            pwdOk
                              ? "text-emerald-600 dark:text-emerald-300"
                              : "text-slate-500 dark:text-slate-400"
                          )}
                        >
                          {strengthLabel}
                        </p>
                      </div>

                      <div className="mt-2 grid grid-cols-5 gap-1.5">
                        {Array.from({ length: 5 }).map((_, index) => (
                          <div
                            key={index}
                            className={cn(
                              "h-1.5 rounded-full transition",
                              passwordStrength > index
                                ? "bg-primary"
                                : "bg-slate-200 dark:bg-white/10"
                            )}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                    >
                      Confirmez le mot de passe
                    </label>

                    <div className="relative">
                      <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                      <input
                        id="confirmPassword"
                        type={showConfirm ? "text" : "password"}
                        placeholder="Confirmez votre mot de passe"
                        className={cn(
                          "h-12 w-full rounded-2xl border py-3 pl-11 pr-14 text-sm font-semibold outline-none transition",
                          "bg-white text-slate-900 placeholder:text-slate-400",
                          "focus:ring-4 focus:ring-primary/10",
                          "dark:bg-slate-950/70 dark:text-white dark:placeholder:text-slate-500",
                          confirmPassword.length && !matchOk
                            ? "border-rose-300 focus:border-rose-500"
                            : "border-slate-200 focus:border-primary dark:border-white/10",
                          !tokenValid || checkingToken
                            ? "cursor-not-allowed opacity-60"
                            : ""
                        )}
                        value={confirmPassword}
                        onChange={(event) => {
                          setConfirmPassword(event.target.value);
                          clearMessages();
                        }}
                        required
                        autoComplete="new-password"
                        disabled={!tokenValid || checkingToken || submitting}
                      />

                      <button
                        type="button"
                        onClick={() => setShowConfirm((value) => !value)}
                        className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-white/10"
                        aria-label={
                          showConfirm
                            ? "Masquer la confirmation"
                            : "Afficher la confirmation"
                        }
                        disabled={!tokenValid || checkingToken || submitting}
                      >
                        {showConfirm ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>

                    {confirmPassword.length > 0 && !matchOk && (
                      <p className="mt-2 flex items-center gap-2 text-xs font-semibold text-rose-600 dark:text-rose-300">
                        <FaExclamationTriangle className="shrink-0 text-[11px]" />
                        Les mots de passe ne correspondent pas.
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className={cn(
                      "inline-flex w-full items-center justify-center gap-3 rounded-full px-6 py-4 text-sm font-bold text-white shadow-lg transition",
                      "bg-primary hover:-translate-y-0.5 hover:brightness-110",
                      "focus:outline-none focus:ring-4 focus:ring-primary/20",
                      "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:brightness-100"
                    )}
                  >
                    {submitting ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Réinitialisation...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle />
                        Réinitialiser le mot de passe
                      </>
                    )}
                  </button>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                    <div className="flex items-start gap-3">
                      <FaInfoCircle className="mt-1 shrink-0 text-primary" />
                      <p>
                        Pour plus de sécurité, utilisez un mot de passe unique,
                        d’au moins <strong>10 caractères</strong>, avec lettres,
                        chiffres et caractères spéciaux.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3 pt-1 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-2 font-bold text-primary transition hover:underline"
                    >
                      <FaSignInAlt className="text-xs" />
                      Retour connexion
                    </Link>

                    <Link
                      to="/register"
                      className="inline-flex items-center gap-2 font-bold text-slate-600 transition hover:text-primary dark:text-slate-300"
                    >
                      <FaUserPlus className="text-xs" />
                      Créer un compte
                    </Link>
                  </div>
                </form>

                <p className="mt-8 text-center text-xs leading-5 text-slate-400">
                  Le lien de réinitialisation est temporaire. S’il a expiré,
                  demandez un nouveau lien depuis la page mot de passe oublié.
                </p>

                {!tokenValid && !checkingToken && (
                  <div className="mt-5 text-center">
                    <Link
                      to="/forgot-password"
                      className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-5 py-3 text-sm font-bold text-primary transition hover:bg-primary hover:text-white"
                    >
                      <FaEnvelope className="text-xs" />
                      Demander un nouveau lien
                    </Link>
                  </div>
                )}
              </div>
            </section>
          </div>

          <p className="mt-5 text-center text-xs font-semibold text-white/70">
            MonAppliClub • Réinitialisation sécurisée
          </p>
        </div>
      </div>
    </main>
  );
}

export default ResetPwd;